import { Modal } from 'components/core-ui/Modal'
import { Stepper } from '@fullscreendigital/carrefour-dex-fe'
import { useMemo, useState } from 'react'
import { useFormik } from 'formik'
import {
  getAssignmentValidationSchema,
  getInitialAssignmentValues,
  getInitialPartnerValues,
  getPartnerValidationSchema
} from './helpers'
import { EditPartnerStep } from './EditPartnerStep'
import { EditAssignmentStep } from './EditAssignmentStep'
import { useMutation } from 'react-query'
import { useNotification } from 'hooks'
import { handleQueryError } from 'utils/helpers'
import { makeCorePostRequest } from 'utils/api'
import { toAssignmentPayload } from './serializers'
import { useStatusAssignmentQuery } from '../../api'

const NewAssignmentModal = props => {
  const {
    isModalOpen,
    closeModal,
    externalPartnerTypeOptions,
    isPartnerCreation,
    setShouldRefreshData
  } = props

  const [activeStep, setActiveStep] = useState(0)

  const { showNotification } = useNotification()

  const initialPartnerValues = useMemo(() => getInitialPartnerValues(), [])

  const partnerValidationSchema = useMemo(() => getPartnerValidationSchema(), [])

  const partnerFormik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialPartnerValues,
    validationSchema: partnerValidationSchema,
    onSubmit: async () => {
      partnerFormik.validateForm()

      if (partnerFormik.isValid) {
        handleNext()
      }
    }
  })

  const initialAssignmentValues = useMemo(() => getInitialAssignmentValues(), [])

  const assignmentValidationSchema = useMemo(
    () => getAssignmentValidationSchema(partnerFormik.values.externalPartnerType),
    [partnerFormik.values.externalPartnerType]
  )

  const { data: statusData, isLoading: statusLoading } = useStatusAssignmentQuery(
    showNotification,
    '/iam/external-partners/statuses'
  )

  const mutateAssignment = async values => {
    await makeCorePostRequest('/iam/external-partners/assignments', values)
  }

  const { isLoading, mutate } = useMutation(mutateAssignment, {
    onSuccess: res => {
      setShouldRefreshData(true)
      cancelModal()
      showNotification({
        message: 'Assignment saved successfully.',
        type: 'success'
      })
    },
    onError: error =>
      handleQueryError(
        error,
        'There was an error saving the assignment. Please try again.',
        showNotification
      )
  })

  const assignmentFormik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: initialAssignmentValues,
    validationSchema: assignmentValidationSchema,
    onSubmit: async values => {
      mutate(
        toAssignmentPayload({
          ...values,
          ...partnerFormik.values,
          status: statusData?.list.find(s => s.name === 'Active')?.id
        })
      )
    }
  })

  const handleNext = () => {
    setActiveStep(prevActiveStep => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep(prevActiveStep => prevActiveStep - 1)
  }

  const cancelModal = () => {
    closeModal()
    partnerFormik.resetForm()
    assignmentFormik.resetForm()
    setActiveStep(0)
  }

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={cancelModal}
      modalTitle="New Assignment"
      modalWidth="50%"
    >
      <form>
        <Stepper
          orientation="vertical"
          activeStepIndex={activeStep}
          steps={[
            {
              label: 'Partner Details',
              content: (
                <EditPartnerStep
                  formik={partnerFormik}
                  assignmentFormik={assignmentFormik}
                  externalPartnerTypeOptions={externalPartnerTypeOptions}
                  isPartnerCreation={isPartnerCreation}
                />
              )
            },
            {
              label: 'Assignment Details',
              content: (
                <EditAssignmentStep
                  onBack={handleBack}
                  formik={assignmentFormik}
                  partnerFormik={partnerFormik}
                  externalPartnerType={partnerFormik.values.externalPartnerType}
                  isSubmitting={isLoading}
                />
              )
            }
          ]}
        />
      </form>
    </Modal>
  )
}

export default NewAssignmentModal
